import { useMemo, useState } from "react";
import Modal from "../../../components/base/Modal";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { formatMoney } from "../lib/money";
import { submitBooking } from "../services/bookingService";
import { providerSupportsBooking, type PaymentDetails } from "../booking/bookingEngine";
import type { BookingQuote, BookingStatus, CargoBooking } from "../booking/bookingTypes";

// ===========================================================================
// BookingCheckout — compra de espacio de carga end-to-end (módulo A)
// ---------------------------------------------------------------------------
// Drives the transaction the quoter hands off: collect payment details, run the
// booking engine (reservar → pagar → confirmar) with a live step indicator, and
// render the confirmation (booking ref + AWB/BL + documentos) or the failure +
// rollback note. The whole purchase happens here, in-app, "donde el proveedor
// lo soporte" — quote-only providers are blocked up front with a clear message.
// ===========================================================================

interface BookingCheckoutProps {
  quote: BookingQuote;
  onClose: () => void;
}

// The four happy-path steps shown in the progress rail.
const STEPS: { status: BookingStatus; label: string; icon: string }[] = [
  { status: "reserved", label: "Reserva", icon: "ri-calendar-check-line" },
  { status: "paid", label: "Pago", icon: "ri-bank-card-line" },
  { status: "confirmed", label: "Confirmación", icon: "ri-checkbox-circle-line" },
];

type Phase = "form" | "processing" | "done";

export default function BookingCheckout({ quote, onClose }: BookingCheckoutProps) {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const supported = useMemo(() => providerSupportsBooking(quote.provider), [quote.provider]);

  const [phase, setPhase] = useState<Phase>("form");
  const [method, setMethod] = useState("card");
  const [cardNumber, setCardNumber] = useState("4242 4242 4242 4242");
  const [holder, setHolder] = useState("");
  const [liveStatus, setLiveStatus] = useState<BookingStatus | null>(null);
  const [liveMessage, setLiveMessage] = useState("");
  const [result, setResult] = useState<CargoBooking | null>(null);

  const inputBase =
    "w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors bg-app border-line text-content focus:border-primary-500";

  // Index of the last reached happy-path step (for the rail's checks).
  const reachedIdx = useMemo(() => {
    if (!liveStatus) return -1;
    const order: BookingStatus[] = ["reserving", "reserved", "payment_pending", "paid", "confirmed"];
    const pos = order.indexOf(liveStatus);
    // Map fine-grained status to the 3 visible steps.
    if (liveStatus === "confirmed") return 3;
    if (pos >= order.indexOf("paid")) return 2;
    if (pos >= order.indexOf("reserved")) return 1;
    return 0;
  }, [liveStatus]);

  const handleConfirm = async () => {
    setPhase("processing");
    const payment: PaymentDetails = { method, cardNumber, holder: holder || "Titular" };
    const booking = await submitBooking(quote, payment, (status, message) => {
      setLiveStatus(status);
      setLiveMessage(message);
    });
    setResult(booking);
    setPhase("done");
  };

  const succeeded = result?.status === "confirmed";

  const content = (
    <div className="space-y-5">
      {/* Quote summary — always visible */}
      <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
        <div className="mb-2 flex items-center justify-between">
          <span className={`text-sm font-semibold ${tc.text}`}>
            {quote.origin || "—"} → {quote.destination || "—"}
          </span>
          <span className="rounded-full bg-primary-500/15 px-2.5 py-0.5 text-xs font-medium text-primary-500">
            {quote.mode}
          </span>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <div className="flex justify-between"><dt className={tc.textSecondary}>Base</dt><dd className={tc.text}>{quote.basis}</dd></div>
          <div className="flex justify-between"><dt className={tc.textSecondary}>Incoterm</dt><dd className={tc.text}>{quote.incoterm}</dd></div>
          <div className="flex justify-between"><dt className={tc.textSecondary}>Proveedor</dt><dd className={tc.text}>{quote.provider || "Por asignar"}</dd></div>
          <div className="flex justify-between"><dt className={tc.textSecondary}>Tránsito</dt><dd className={tc.text}>{quote.transitDays}</dd></div>
        </dl>
        <div className="mt-3 flex items-end justify-between border-t border-dashed border-gray-400/30 pt-3">
          <span className={`text-xs ${tc.textSecondary}`}>Total a pagar</span>
          <span className="text-xl font-bold text-primary-500">{formatMoney(quote.amount, quote.currency)}</span>
        </div>
      </div>

      {/* Provider does not support online booking */}
      {!supported && phase === "form" && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-600 dark:text-amber-400">
          <i className="ri-error-warning-line mr-1" />
          El proveedor <b>{quote.provider}</b> no soporta compra en línea; solo cotización.
          Solicita la reserva por el canal del proveedor.
        </div>
      )}

      {/* Payment form */}
      {supported && phase === "form" && (
        <div className="space-y-3">
          <div>
            <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Método de pago</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "card", l: "Tarjeta", i: "ri-bank-card-line" },
                { v: "wallet", l: "Wallet", i: "ri-wallet-3-line" },
                { v: "credit", l: "Crédito", i: "ri-bill-line" },
              ].map((m) => (
                <button
                  key={m.v}
                  onClick={() => setMethod(m.v)}
                  className={`flex items-center justify-center gap-1 rounded-lg border px-2 py-2 text-xs transition-colors ${
                    method === m.v
                      ? "border-primary-500 bg-primary-500/10 text-primary-500"
                      : `${tc.cardBorder} ${tc.textSecondary} hover:border-primary-500/50`
                  }`}
                >
                  <i className={m.i} />{m.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Número de tarjeta</label>
            <input value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} placeholder="4242 4242 4242 4242" className={inputBase} />
            <p className={`mt-1 text-[11px] ${tc.textSecondary}`}>
              Demostración: una tarjeta terminada en <b>0000</b> simula un pago rechazado (con rollback).
            </p>
          </div>
          <div>
            <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Titular</label>
            <input value={holder} onChange={(e) => setHolder(e.target.value)} placeholder="Nombre del titular" className={inputBase} />
          </div>
        </div>
      )}

      {/* Progress rail (processing + done) */}
      {phase !== "form" && (
        <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
          <div className="flex items-center justify-between">
            {STEPS.map((step, i) => {
              const done = reachedIdx > i || (succeeded && i === STEPS.length - 1);
              const active = reachedIdx === i + 1 && phase === "processing";
              const failed = phase === "done" && !succeeded && reachedIdx <= i;
              return (
                <div key={step.status} className="flex flex-1 flex-col items-center text-center">
                  <span
                    className={`flex h-9 w-9 items-center justify-center rounded-full text-base ${
                      failed
                        ? "bg-red-500/15 text-red-500"
                        : done
                          ? "bg-emerald-500/15 text-emerald-500"
                          : active
                            ? "bg-primary-500/15 text-primary-500"
                            : `bg-subtle ${tc.textSecondary}`
                    }`}
                  >
                    <i className={done ? "ri-check-line" : active ? "ri-loader-4-line animate-spin" : step.icon} />
                  </span>
                  <span className={`mt-1 text-[11px] ${tc.textSecondary}`}>{step.label}</span>
                </div>
              );
            })}
          </div>
          {liveMessage && (
            <p className={`mt-3 text-center text-xs ${succeeded || phase === "processing" ? tc.textSecondary : "text-red-500"}`}>
              {liveMessage}
            </p>
          )}
        </div>
      )}

      {/* Confirmation / documents */}
      {phase === "done" && succeeded && result && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4">
          <div className="mb-2 flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
            <i className="ri-checkbox-circle-fill text-lg" />
            <span className="text-sm font-semibold">Compra completada · Folio {result.folio}</span>
          </div>
          <dl className="space-y-1.5 text-xs">
            <div className="flex justify-between"><dt className={tc.textSecondary}>Referencia de reserva</dt><dd className={`font-mono ${tc.text}`}>{result.bookingRef}</dd></div>
            {result.documents.map((d) => (
              <div key={d.type} className="flex justify-between">
                <dt className={tc.textSecondary}>{d.label}</dt>
                <dd className={`font-mono ${tc.text}`}>{d.reference}</dd>
              </div>
            ))}
            <div className="flex justify-between"><dt className={tc.textSecondary}>Estado de pago</dt><dd className="font-medium text-emerald-600 dark:text-emerald-400">Pagado</dd></div>
          </dl>
        </div>
      )}

      {/* Failure / rollback */}
      {phase === "done" && !succeeded && result && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm">
          <div className="mb-1 flex items-center gap-2 text-red-500">
            <i className="ri-close-circle-fill text-lg" />
            <span className="font-semibold">No se pudo completar la compra</span>
          </div>
          <p className={tc.textSecondary}>
            La transacción se revirtió por completo: no se realizó ningún cargo y el espacio
            reservado fue liberado. Puedes intentar de nuevo.
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-1">
        {phase === "form" && (
          <>
            <button onClick={onClose} className={`rounded-lg border px-4 py-2 text-sm ${tc.cardBorder} ${tc.textSecondary} ${tc.buttonHover}`}>
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={!supported}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <i className="ri-secure-payment-line" />
              Confirmar reserva y pago
            </button>
          </>
        )}
        {phase === "processing" && (
          <button disabled className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary opacity-70">
            <i className="ri-loader-4-line animate-spin" /> Procesando…
          </button>
        )}
        {phase === "done" && (
          <>
            {!succeeded && (
              <button
                onClick={() => { setPhase("form"); setLiveStatus(null); setLiveMessage(""); setResult(null); }}
                className={`rounded-lg border px-4 py-2 text-sm ${tc.cardBorder} ${tc.textSecondary} ${tc.buttonHover}`}
              >
                Reintentar
              </button>
            )}
            <button onClick={onClose} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400">
              {succeeded ? "Listo" : "Cerrar"}
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      title="Compra de espacio de carga"
      onClose={onClose}
      themeClasses={tc}
      width="max-w-lg"
      content={content}
    />
  );
}
